import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from './auth'
import {createSlice} from "@reduxjs/toolkit";
import {useSelector} from "react-redux";
import {rootReducer} from "../store";

export const NOTIFICATIONS_REQUEST: NotificationsRequest = {
  page: 1,
  limit: 10,
}

export const notificationsApi = createApi({
  baseQuery: baseQuery(),
  reducerPath: 'notificationsApi',
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationsResponse, NotificationsRequest>({
      query: ({ page, limit, search, section }) => ({
        url: 'support/v4.0/notifications/',
        params: { page, limit, search, section },
      }),
    }),
    getNotification: builder.query<NotificationItem, string>({
      query: (id) => ({ url: `support/v4.0/notifications/${id}/` }),
    }),
    getNotificationSections: builder.query<NotificationSection[], void>({
      query: () => ({ url: 'support/v4.0/meta/notification-sections/' }),
    }),
    readNotifications: builder.mutation<NotificationsStatusResponse, NotificationsStatusRequest>({
      query: (body) => ({ url: 'support/v4.0/notifications/', method: 'POST', body }),
    }),
    unreadNotifications: builder.mutation<NotificationsStatusResponse, NotificationsStatusRequest>({
      query: (body) => ({ url: 'support/v4.0/notifications/unread/', method: 'POST', body }),
    }),
  }),
})

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications: {} as NotificationsResponse,
    notification: {} as NotificationItem,
    sections: [] as NotificationSection[],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      notificationsApi.endpoints.getNotifications.matchFulfilled,
      (state, { payload }) => {
        state.notifications = payload
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.getNotification.matchFulfilled,
      (state, { payload }) => {
        state.notification = payload
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.getNotificationSections.matchFulfilled,
      (state, { payload }) => {
        state.sections = payload
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.readNotifications.matchFulfilled,
      (state, { meta: { arg: { originalArgs } } }) => {
        const { ids } = originalArgs;
        const notifications = state.notifications.results;

        state.notifications.results = notifications?.map(notification => {
          const isSelected = ids.includes(notification.id);

          return isSelected ? { ...notification, is_unread: false } : notification;
        });

        const isSelectedNotification = ids.includes(state.notification.id);

        if (isSelectedNotification) {
          state.notification = { ...state.notification, is_unread: false }
        }
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.unreadNotifications.matchFulfilled,
      (state, { meta: { arg: { originalArgs } } }) => {
        const { ids } = originalArgs;
        const notifications = state.notifications.results;

        state.notifications.results = notifications?.map(notification => {
          const isSelected = ids.includes(notification.id);

          return isSelected ? { ...notification, is_unread: true } : notification;
        });

        const isSelectedNotification = ids.includes(state.notification.id);

        if (isSelectedNotification) {
          state.notification = { ...state.notification, is_unread: true }
        }
      }
    );
  },
})

export const {
  useGetNotificationsQuery,
  useGetNotificationSectionsQuery,
  useGetNotificationQuery,
  useReadNotificationsMutation,
  useUnreadNotificationsMutation,
} = notificationsApi

export const useNotifications = () => {
  return useSelector(({ notifications }: ReturnType<typeof rootReducer>) => notifications.notifications)
}

export const useNotification = () => {
  return useSelector(({ notifications }: ReturnType<typeof rootReducer>) => notifications.notification)
}

export const useNotificationSections = () => {
  return useSelector(({ notifications }: ReturnType<typeof rootReducer>) => notifications.sections)
}
